import React from 'react';
import { get } from 'lodash';
import { graphql, Link } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import { Social } from '@/components/Social';
import { CallToAction } from '@/components/CallToAction';
import Layout, { Main } from '@/components/Layout';
import { Content, HTMLContent } from '@/components/Content';
import { Seo } from '@/components/SEO';
import styled from 'styled-components';
import { rem } from 'polished';
import {
  faArrowLeftLong,
  faFileContract,
} from '@fortawesome/pro-light-svg-icons';
import { Text } from '@/components/Text';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MdContent } from '@/components/MdContent';
import { Button } from '@/components/Button';

interface IPrivacyPage {
  data: {
    policyPage: any;
    privacyPolicy: any;
  };
}

interface IPrivacyPageTemplate {
  privacyPolicy: any;
}

interface IHeaderSection {
  title?: string;
  body?: any;
}

export const StyledPrivacy = styled.div`
  .main-page-container {
    padding: 0;
    margin-bottom: ${rem(`48px`)};
    ${({ theme }) => theme.breakpoints.phablet`
      padding: ${rem(`16px`)};
    `}
    ${({ theme }) => theme.breakpoints.desktop`
      margin-top: ${rem(`48px`)};
    `}
  }
  .main-page-content {
    padding: ${rem(`16px`)};
    border-radius: ${rem(`12px`)};
    background-color: ${({ theme }) => theme.colors.blue_1_65};
    ${({ theme }) => theme.breakpoints.desktop`
      padding: ${rem(`48px`)} ${rem(`48px`)} ${rem(`48px`)} ${rem(`24px`)};
    `}
  }
  .content-html {
    margin-bottom: 0;
  }
  && .heading .content {
    padding-top: ${rem(60)};
  }
  .main-header {
    padding: ${rem(`16px`)} 0 0;
  }
  .cta {
    margin-bottom: ${rem(`24px`)};
  }
  .content {
    display: grid;
    overflow: hidden;
    grid-template-columns: repeat(1, auto);
    grid-template-rows: 1fr;
    gap: ${rem(`12px`)};
    ${({ theme }) => theme.breakpoints.phablet`
      gap: ${rem(`24px`)};
    `}
  }
  .heading-container {
    padding: 0 ${rem(`16px`)};
    margin-top: ${rem(`24px`)};
    margin-bottom: ${rem(`24px`)};
    ${({ theme }) => theme.breakpoints.desktop`
      margin-top: 0;
      margin-bottom: 0;
    `}
  }
  .title {
    display: flex;
    margin-bottom: 0;
  }
  .title__icon {
    margin-top: ${rem(`16px`)};
    margin-right: ${rem(`12px`)};
    path {
      fill: url('#rainbow_1_svg') #fff;
    }
  }
  .back-button {
    margin-bottom: ${rem(`24px`)};
    color: ${({ theme }) => theme.colors.white_1_45};
    padding: ${rem(`8px`)} ${rem(`12px`)};
    background-color: ${({ theme }) => theme.colors.blue_1_65};
    border-radius: ${rem(`12px`)};
    &:hover {
      color: ${({ theme }) => theme.colors.pink_1};
    }
    svg path {
      fill: ${({ theme }) => theme.colors.pink_1};
    }
  }
  .back-content {
    margin-left: ${rem(`4px`)};
  }
`;

const HeaderSection = ({ title, body }: IHeaderSection) => {
  return (
    <div className="heading-container">
      <Link to="/" className="back-button">
        <FontAwesomeIcon icon={faArrowLeftLong} />
        <span className="back-content">Back to site</span>
      </Link>
      <Content restrictWidth noMargin className="main-header">
        <div className="title">
          <FontAwesomeIcon
            size="4x"
            className="title__icon"
            icon={faFileContract}
          />
          <Text type="title1" tag="h2" color="white_1">
            {title}
          </Text>
        </div>
      </Content>
      <Text type="body1" tag="div" color="gray_3">
        <MdContent content={body.body} />
      </Text>
    </div>
  );
};
const PrivacyPageDesktopTemplate = ({
  privacyPolicy,
}: IPrivacyPageTemplate) => {
  const content = get(privacyPolicy.body, `childMarkdownRemark.html`);
  return (
    <>
      <div className="main-page-container">
        <Content restrictWidth noMargin className="main-page-content">
          <Text type="body1" color="gray_3">
            <HTMLContent className="content-html">{content}</HTMLContent>
          </Text>
        </Content>
      </div>

      <Content restrictWidth shouldCenter noMargin>
        <Link to="/">
          <Button size="large" disabled={false}>
            Back to Home
          </Button>
        </Link>
      </Content>
    </>
  );
};

const PrivacyPage = ({ data }: IPrivacyPage) => {
  const privacyPolicyData = get(data, `privacyPolicy`);
  const page = get(data, `policyPage`);
  const {
    heroImage,
    svgTopPath,
    svgBottomPath,
    ctaTitle,
    ctaHookId,
    ctaIcon: pageCTAIcon,
    ctaBody,
    body,
  } = page;
  const { title } = privacyPolicyData;
  const image = getImage(heroImage);
  const ctaIcon = getImage(pageCTAIcon);

  return (
    <StyledPrivacy>
      <Layout
        location={{ pathname: `/privacy` }}
        coverImage={image}
        variant={1}
        topSvg={svgTopPath.svg.content}
        bottomSvg={svgBottomPath.svg.content}
        desktopHeading={<HeaderSection title={title} body={body} />}
        desktopBody={
          <PrivacyPageDesktopTemplate privacyPolicy={privacyPolicyData} />
        }
        desktopCta={
          <Content>
            <CallToAction
              hookId={ctaHookId}
              icon={ctaIcon}
              title={ctaTitle}
              body={ctaBody.ctaBody}
              maxWidth="100%"
              className="cta"
            />
            <Social />
          </Content>
        }
      >
        <Seo
          title={`Tags | ${title}`}
          metaTitle={`Tags | ${title}`}
          metaDesc={title}
          cover={heroImage}
          slug="/tags"
          templateKey="tags"
        />
        <Main background="transparent" disableVertPadding={false}>
          <HeaderSection title={title} body={body} />
          <PrivacyPageDesktopTemplate privacyPolicy={privacyPolicyData} />
        </Main>
      </Layout>
    </StyledPrivacy>
  );
};

export default PrivacyPage;

export const PolicyPageQuery = graphql`
  query PolicyQuery {
    policyPage: contentfulPage(slug: { eq: "policy" }) {
      title
      heroImage {
        gatsbyImageData(
          width: 560
          height: 900
          quality: 85
          placeholder: BLURRED
        )
      }
      svgTopPath {
        svg {
          content
        }
      }
      svgBottomPath {
        svg {
          content
        }
      }
      ctaTitle
      ctaHookId
      ctaIcon {
        gatsbyImageData(width: 48, quality: 100, placeholder: BLURRED)
      }
      ctaBody {
        ctaBody
      }
      body {
        body
      }
    }
    privacyPolicy: contentfulPolicy(slug: { eq: "privacy-policy" }) {
      title
      slug
      lastUpdate
      version
      body {
        childMarkdownRemark {
          html
        }
      }
    }
  }
`;
